import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ICoupon, ICouponFormData } from '../../types/coupon';
import { couponService } from '../../services/couponService';
import { authService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';

interface CouponEditDialogProps {
  open: boolean;
  onClose: () => void;
  coupon: ICoupon | null;
  onSaved: () => void;
}

const CouponEditDialog: React.FC<CouponEditDialogProps> = ({
  open,
  onClose,
  coupon,
  onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { hasRole } = useAuth();
  const canEditNotificationDetails = !hasRole(['coupon:tester']);

  const getInitialFormData = (): ICouponFormData => {
    const now = new Date();
    let notificationText = coupon?.notificationText || '';
    let couponDescription = coupon?.description || '';
    let accountNumbers = coupon?.accountNumbers || '';

    // Auto-populate for tester role
    if (hasRole(['coupon:tester'])) {
      accountNumbers = authService.getNotificationsAllowedAccounts()?.join('\n') || '';

      if (!coupon) {
        const timeString = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()} sent ${now.getHours()}:${now.getMinutes()}`;
        notificationText = 'This is a Test Title ' + timeString;
        couponDescription = 'This is a Test Description ' + timeString;
      }
    }

    return {
      couponId: coupon?.couponId,
      couponCode: coupon?.couponCode || '',
      triggerType: coupon?.triggerType || 'assignment',
      discountType: coupon?.discountType || 'percentage',
      discountValue: coupon?.discountValue || 0,
      maximumRedemptions: coupon?.maximumRedemptions || 1,
      notificationText,
      notificationUrl: coupon?.notificationUrl || '',
      couponDescription,
      imageUrl: coupon?.imageUrl || '',
      startDate: coupon?.dateStart ? new Date(coupon.dateStart) : null,
      startTime: coupon?.dateStart
        ? new Date(coupon.dateStart).toTimeString().substring(0, 5)
        : '00:00',
      endDate: coupon?.dateEnd ? new Date(coupon.dateEnd) : null,
      endTime: coupon?.dateEnd
        ? new Date(coupon.dateEnd).toTimeString().substring(0, 5)
        : '23:59',
      isScheduledTask: coupon?.isScheduledTask ? true : false,
      scheduledTaskStartDate: coupon?.scheduledTaskStart
        ? new Date(coupon.scheduledTaskStart)
        : null,
      scheduledTaskStartTime: coupon?.scheduledTaskStart
        ? new Date(coupon.scheduledTaskStart).toTimeString().substring(0, 5)
        : '00:00',
      accountNumbers,
      productCodes: coupon?.productCodes || '',
      maxCount: coupon?.maxCount ?? 99999,
    };
  };

  const [formData, setFormData] = useState<ICouponFormData>(getInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData());
      setErrors({});
      setImagePreview(null);
      setImageError(null);
    }
  }, [open, coupon]);

  // Load and preview image when imageUrl changes
  useEffect(() => {
    const loadImage = async () => {
      if (!formData.imageUrl || !formData.imageUrl.match(/^https?:\/\/.+/)) {
        setImagePreview(null);
        setImageError(null);
        return;
      }

      setImageLoading(true);
      setImageError(null);
      setImagePreview(null);

      const tryLoadImage = (useCors: boolean): Promise<boolean> => {
        return new Promise((resolve) => {
          const img = new Image();
          if (useCors) {
            img.crossOrigin = 'anonymous';
          }

          const timeout = setTimeout(() => {
            resolve(false);
          }, 10000); // 10 second timeout

          img.onload = () => {
            clearTimeout(timeout);
            setImagePreview(formData.imageUrl);
            setImageError(null);
            resolve(true);
          };

          img.onerror = () => {
            clearTimeout(timeout);
            resolve(false);
          };

          img.src = formData.imageUrl;
        });
      };

      try {
        // First try without CORS (works for same-origin or servers that allow it)
        let loaded = await tryLoadImage(false);

        // If that fails, try with CORS
        if (!loaded) {
          loaded = await tryLoadImage(true);
        }

        // If both fail, it might be a CORS issue but the URL could still be valid
        if (!loaded) {
          // Don't set preview, but show a helpful message
          setImagePreview(null);
          setImageError('Unable to preview image (may be due to CORS restrictions). The URL will be saved and should work when the coupon is used.');
        }
      } catch (error) {
        // Even on error, set the preview URL as it might still be valid
        setImagePreview(formData.imageUrl);
        setImageError('Unable to verify image. The URL will be saved and may work when the coupon is used.');
      } finally {
        setImageLoading(false);
      }
    };

    // Debounce the image loading
    const timeoutId = setTimeout(() => {
      loadImage();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.imageUrl]);

  const handleChange = (field: keyof ICouponFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.couponCode) newErrors.couponCode = 'Please provide a coupon code';
    if (!formData.discountType) newErrors.discountType = 'Please select a discount type';
    if (!formData.discountValue || formData.discountValue < 0.01 || formData.discountValue > 10000) {
      newErrors.discountValue = 'Discount value must be between 0.01 and 10000';
    }
    if (!formData.maximumRedemptions || formData.maximumRedemptions < 1 || formData.maximumRedemptions > 100) {
      newErrors.maximumRedemptions = 'Maximum redemptions must be between 1 and 100';
    }
    if (!formData.notificationText) newErrors.notificationText = 'Please provide notification text';
    if (!formData.couponDescription) newErrors.couponDescription = 'Please provide description';
    if (formData.notificationUrl && !formData.notificationUrl.match(/^https?:\/\/.+/)) {
      newErrors.notificationUrl = 'Please provide valid Notification URL';
    }
    // Image URL validation - check format but don't block if image fails to load
    if (!formData.imageUrl || !formData.imageUrl.match(/^https?:\/\/.+/)) {
      newErrors.imageUrl = 'Please provide valid Image URL';
    }
    if (!formData.startDate) newErrors.startDate = 'Please provide start date';
    if (!formData.startTime) newErrors.startTime = 'Please provide start time';
    if (!formData.endDate) newErrors.endDate = 'Please provide end date';
    if (!formData.endTime) newErrors.endTime = 'Please provide end time';
    if (formData.maxCount < 0 || formData.maxCount > 99999) {
      newErrors.maxCount = 'Max count must be between 0 and 99999';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const combineDateAndTime = (date: Date | null, time: string): Date => {
    const combined = new Date(date || new Date());
    const [hours, minutes] = time.split(':');
    combined.setHours(parseInt(hours, 10));
    combined.setMinutes(parseInt(minutes, 10));
    return combined;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const combinedStartDate = combineDateAndTime(formData.startDate, formData.startTime);
      const combinedEndDate = combineDateAndTime(formData.endDate, formData.endTime);

      let isScheduledTask = formData.isScheduledTask ? 1 : 0;
      let scheduledTaskStart: string | null = null;

      if (formData.triggerType === 'assignment' && formData.isScheduledTask && formData.scheduledTaskStartDate) {
        scheduledTaskStart = combineDateAndTime(
          formData.scheduledTaskStartDate,
          formData.scheduledTaskStartTime
        ).toISOString();
      } else {
        isScheduledTask = 0;
      }

      await couponService.saveCoupon(
        formData.couponId,
        'coupon',
        formData.triggerType,
        formData.couponCode,
        formData.notificationText,
        formData.notificationUrl,
        formData.couponDescription,
        formData.imageUrl,
        combinedStartDate.toISOString(),
        combinedEndDate.toISOString(),
        formData.maximumRedemptions,
        formData.maxCount,
        formData.discountType,
        formData.discountValue,
        formData.accountNumbers,
        formData.productCodes,
        isScheduledTask,
        scheduledTaskStart
      );

      onSaved();
      onClose();
    } catch (error: any) {
      console.error('Error saving coupon:', error);
      alert('Network error. Try again later');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: '95vh', sm: '90vh' },
          width: { xs: '100%', sm: 'auto', md: '90%', lg: '85%' }
        }
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        {coupon ? 'Edit Coupon' : 'Create Coupon'}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 }, overflowY: 'auto' }}>
        <Box sx={{ pt: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Coupon Code"
                value={formData.couponCode}
                onChange={(e) => handleChange('couponCode', e.target.value)}
                error={!!errors.couponCode}
                helperText={errors.couponCode}
                required
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  select
                  label="Coupon Type"
                  value={formData.discountType}
                  onChange={(e) => handleChange('discountType', e.target.value)}
                  error={!!errors.discountType}
                  helperText={errors.discountType}
                  required
                >
                  <MenuItem value="percentage">Percentage Discount</MenuItem>
                  <MenuItem value="amount">Amount Discount</MenuItem>
                </TextField>
              </Box>

              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Coupon Value"
                  value={formData.discountValue}
                  onChange={(e) => handleChange('discountValue', parseFloat(e.target.value))}
                  error={!!errors.discountValue}
                  helperText={errors.discountValue}
                  inputProps={{ min: 0.01, max: 10000, step: 0.01 }}
                  required
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Redemptions"
                  value={formData.maximumRedemptions}
                  onChange={(e) => handleChange('maximumRedemptions', parseInt(e.target.value, 10))}
                  error={!!errors.maximumRedemptions}
                  helperText={errors.maximumRedemptions}
                  inputProps={{ min: 1, max: 100 }}
                  required
                />
              </Box>

              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum No of Sends"
                  value={formData.maxCount}
                  onChange={(e) => handleChange('maxCount', parseInt(e.target.value, 10))}
                  error={!!errors.maxCount}
                  helperText={errors.maxCount}
                  inputProps={{ min: 0, max: 99999 }}
                  required
                />
              </Box>
            </Box>

            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Notification Title"
                value={formData.notificationText}
                onChange={(e) => handleChange('notificationText', e.target.value)}
                error={!!errors.notificationText}
                helperText={errors.notificationText}
                required
              />
            </Box>

            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.couponDescription}
                onChange={(e) => handleChange('couponDescription', e.target.value)}
                error={!!errors.couponDescription}
                helperText={errors.couponDescription}
                inputProps={{ maxLength: 500 }}
                required
              />
            </Box>

            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Notification URL"
                value={formData.notificationUrl}
                onChange={(e) => handleChange('notificationUrl', e.target.value)}
                error={!!errors.notificationUrl}
                helperText={errors.notificationUrl}
              />
            </Box>

            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Image URL"
                value={formData.imageUrl}
                onChange={(e) => handleChange('imageUrl', e.target.value)}
                error={!!errors.imageUrl}
                helperText={errors.imageUrl}
                required
              />
              {imageLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading image...
                  </Typography>
                </Box>
              )}
              {imageError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {imageError}
                </Alert>
              )}
              {imagePreview && !imageLoading && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Image Preview:
                  </Typography>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Coupon preview"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: 'background.paper',
                    }}
                    onError={() => {
                      setImageError('Failed to display image');
                      setImagePreview(null);
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={formData.startDate ? formData.startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleChange('startDate', e.target.value ? new Date(e.target.value) : null)}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>

              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={formData.startTime}
                  onChange={(e) => handleChange('startTime', e.target.value)}
                  error={!!errors.startTime}
                  helperText={errors.startTime}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={formData.endDate ? formData.endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleChange('endDate', e.target.value ? new Date(e.target.value) : null)}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>

              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={formData.endTime}
                  onChange={(e) => handleChange('endTime', e.target.value)}
                  error={!!errors.endTime}
                  helperText={errors.endTime}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>
            </Box>

            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                select
                label="Trigger Type"
                value={formData.triggerType}
                onChange={(e) => handleChange('triggerType', e.target.value)}
                required
              >
                <MenuItem value="assignment">Selected Accounts</MenuItem>
                {canEditNotificationDetails && <MenuItem value="on_entry">On Entry</MenuItem>}
                {canEditNotificationDetails && <MenuItem value="on_exit">On Exit</MenuItem>}
              </TextField>
            </Box>

            {formData.triggerType === 'assignment' && (
              <>
                <Box sx={{ width: '100%' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isScheduledTask}
                        onChange={(e) => handleChange('isScheduledTask', e.target.checked)}
                      />
                    }
                    label="Send at scheduled time"
                  />
                </Box>

                {formData.isScheduledTask && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Scheduled Send Date"
                        value={
                          formData.scheduledTaskStartDate
                            ? formData.scheduledTaskStartDate.toISOString().split('T')[0]
                            : ''
                        }
                        onChange={(e) =>
                          handleChange('scheduledTaskStartDate', e.target.value ? new Date(e.target.value) : null)
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>

                    <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                      <TextField
                        fullWidth
                        type="time"
                        label="Scheduled Send Time"
                        value={formData.scheduledTaskStartTime}
                        onChange={(e) => handleChange('scheduledTaskStartTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}

            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Account Numbers"
                value={formData.accountNumbers}
                onChange={(e) => handleChange('accountNumbers', e.target.value)}
                disabled={!canEditNotificationDetails}
              />
            </Box>

            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Product Codes"
                value={formData.productCodes}
                onChange={(e) => handleChange('productCodes', e.target.value)}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Button
          onClick={onClose}
          disabled={loading}
          fullWidth={isMobile}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading}
          fullWidth={isMobile}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CouponEditDialog;
